import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import Select, { components as ReactSelectComponents } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Icon } from 'ui';
import useSelectCustomizable from './useSelectCustomizable';

export const utilsSelectCustomizable = {
  /**
   * Devuelve las opciones que son necesarias para el componente SelectCustomizable,
   * dichas opciones las compone recorriendo el array elements
   * y accediendo a la propiedad propertyToLabel para el valor label,
   * y a la propieddad propertyToValue para el valor value de las opciones
   * @param {array} elements
   * @param {string} propertyToLabel
   * @param {string} propertyToValue
   */
  getOptionsProperties(elements, propertyToLabel, propertyToValue) {
    return elements && propertyToLabel && propertyToValue
      ? elements.map((element) => {
          return {
            label: element[propertyToLabel],
            value: element[propertyToValue],
          };
        })
      : [];
  },
};

// Componentes custom definidos a nivel de módulo para mantener identidad de referencia
// entre renders y evitar que react-select los desmonte/remonte en cada keystroke.
// Los datos del componente padre llegan a través de selectProps (react-select pasa
// todas las props desconocidas del <Select> en selectProps de cada custom component).

const AccessibleControl = ({ children, innerProps, menuIsOpen, ...controlProps }) => (
  <ReactSelectComponents.Control innerProps={{ ...innerProps }} menuIsOpen={menuIsOpen} {...controlProps}>
    {children}
  </ReactSelectComponents.Control>
);
AccessibleControl.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  menuIsOpen: PropTypes.bool,
};

const AccessibleInput = ({ selectProps, ...inputProps }) => (
  <ReactSelectComponents.Input
    role="combobox"
    aria-haspopup="listbox"
    aria-expanded={selectProps.menuIsOpen ? 'true' : 'false'}
    aria-controls={selectProps._listboxId}
    aria-activedescendant={selectProps._focusedOptionId || undefined}
    selectProps={selectProps}
    {...inputProps}
  />
);
AccessibleInput.propTypes = {
  selectProps: PropTypes.shape({
    menuIsOpen: PropTypes.bool,
    _listboxId: PropTypes.string,
    _focusedOptionId: PropTypes.string,
  }),
};

const AccessibleMenu = ({ children, innerProps, selectProps, ...menuProps }) => (
  <ReactSelectComponents.Menu
    innerProps={{ ...innerProps, role: 'listbox', id: selectProps._listboxId }}
    selectProps={selectProps}
    {...menuProps}
  >
    {children}
  </ReactSelectComponents.Menu>
);
AccessibleMenu.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  selectProps: PropTypes.shape({
    _listboxId: PropTypes.string,
  }),
};

const AccessibleOption = ({ children, innerProps, isSelected, isFocused, selectProps, ...optionProps }) => {
  useEffect(() => {
    if (isFocused && innerProps.id) {
      selectProps._onOptionFocusChange(innerProps.id);
    }
  }, [isFocused, innerProps.id, selectProps._onOptionFocusChange]);

  return (
    <ReactSelectComponents.Option
      innerProps={{ ...innerProps, role: 'option', 'aria-selected': isSelected }}
      isSelected={isSelected}
      isFocused={isFocused}
      selectProps={selectProps}
      {...optionProps}
    >
      {children}
    </ReactSelectComponents.Option>
  );
};
AccessibleOption.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.shape({
    id: PropTypes.string,
  }),
  isSelected: PropTypes.bool,
  isFocused: PropTypes.bool,
  selectProps: PropTypes.shape({
    _onOptionFocusChange: PropTypes.func,
  }),
};

const DropdownIndicator = ({ innerProps, selectProps }) => (
  <div className="select-customizable__indicator " {...innerProps}>
    {selectProps.iconTypeIndicator ? (
      <Icon name={selectProps.iconTypeIndicator} size="sm" color="black" />
    ) : (
      <div className="select-customizable__dropdown-chevron" />
    )}
  </div>
);
DropdownIndicator.propTypes = {
  innerProps: PropTypes.object,
  selectProps: PropTypes.shape({
    iconTypeIndicator: PropTypes.string,
  }),
};

const MultiValueContainer = ({ selectProps }) => {
  return selectProps.showValuesAsList || selectProps.showValuesAsText ? null : undefined;
};
MultiValueContainer.propTypes = {
  selectProps: PropTypes.shape({
    showValuesAsList: PropTypes.bool,
    showValuesAsText: PropTypes.bool,
  }),
};

const InlineValueChip = ({ option, displayLabel, onRemove }) => {
  function handleClick(e) {
    e.stopPropagation();
    onRemove(option);
  }
  return (
    <li className="select-customizable__inline-value" aria-label={option.label}>
      <span className="select-customizable__inline-value-label">{displayLabel}</span>
      <button
        className="select-customizable__inline-value-remove"
        onClick={handleClick}
        type="button"
        aria-label={`Remove ${option.label}`}
      />
    </li>
  );
};
InlineValueChip.propTypes = {
  option: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any,
    shortLabel: PropTypes.string,
  }),
  displayLabel: PropTypes.string,
  onRemove: PropTypes.func,
};

function sortByDisplayLabel(values, useShortLabels) {
  return [...values].sort((a, b) => {
    const la = useShortLabels && a.shortLabel ? a.shortLabel : a.label;
    const lb = useShortLabels && b.shortLabel ? b.shortLabel : b.label;
    return la.localeCompare(lb);
  });
}

// showValuesAsText: resumen de una sola línea ("T1, T2, T3"), sin control para quitar
// valores individuales desde el propio control (se gestiona abriendo el desplegable).
const ValueSummary = ({ children, hasValue, getValue, selectProps }) => {
  if (!hasValue) {
    return <div className="select-customizable__value-container">{children}</div>;
  }

  const sorted = sortByDisplayLabel(getValue(), selectProps.useShortLabels);
  const summary = sorted.map((v) => (selectProps.useShortLabels && v.shortLabel ? v.shortLabel : v.label)).join(', ');

  return (
    <div className="select-customizable__value-container">
      <span className="select-customizable__value-summary" title={summary}>
        {summary}
      </span>
      {children[1]}
    </div>
  );
};
ValueSummary.propTypes = {
  children: PropTypes.node,
  hasValue: PropTypes.bool,
  getValue: PropTypes.func,
  selectProps: PropTypes.shape({
    useShortLabels: PropTypes.bool,
  }),
};

const ValueContainer = ({ children, hasValue, getValue, selectProps }) => {
  if (selectProps.showValuesAsText) {
    return <ValueSummary hasValue={hasValue} getValue={getValue} selectProps={selectProps}>{children}</ValueSummary>;
  }

  if (!selectProps.showValuesAsList) {
    return children;
  }

  if (!hasValue) {
    return (
      <div className="select-customizable__value-container">
        {children}
      </div>
    );
  }

  const values = getValue();
  const sorted = sortByDisplayLabel(values, selectProps.useShortLabels);

  function handleRemoveValue(v) {
    const newValues = values.filter((val) => val.value !== v.value);
    selectProps.onChange(newValues, { action: 'remove-value', removedValue: v });
  }

  return (
    <div className="select-customizable__value-container">
      <ul className="select-customizable__inline-values">
        {sorted.map((v) => {
          const displayLabel = selectProps.useShortLabels && v.shortLabel ? v.shortLabel : v.label;
          return (
            <InlineValueChip
              key={v.value}
              option={v}
              displayLabel={displayLabel}
              onRemove={handleRemoveValue}
            />
          );
        })}
      </ul>
      {children[1]}
    </div>
  );
};
ValueContainer.propTypes = {
  children: PropTypes.node,
  hasValue: PropTypes.bool,
  getValue: PropTypes.func,
  selectProps: PropTypes.shape({
    showValuesAsList: PropTypes.bool,
    showValuesAsText: PropTypes.bool,
    useShortLabels: PropTypes.bool,
    onChange: PropTypes.func,
  }),
};

const SelectCustomizable = ({
  t,
  isMulti,
  options,
  defaultValue,
  value,
  placeholder = t('Select'),
  noOptionsMessage,
  variant,
  onChange,
  label,
  name,
  size,
  error,
  canCreate,
  onCreateOption,
  menuPosition,
  iconTypeIndicator,
  menuPortalTarget,
  useShortLabels,
  hideSelectedOptions = false,
  showValuesAsList = false,
  showValuesAsText = false,
  isSearchable,
  inputId,
  onKeyDown: consumerOnKeyDown,
  onMenuOpen: consumerOnMenuOpen,
  onMenuClose: consumerOnMenuClose,
  ...props
}) => {
  const {
    resolvedInputId,
    labelId,
    listboxId,
    selectRef,
    focusedOptionId,
    onOptionFocusChange,
    handleKeyDown,
    handleMenuOpen,
    handleMenuClose,
    finalIsSearchable,
    finalMenuPlacement,
    getNoOptionsMessage,
    sharedStyles,
  } = useSelectCustomizable({ inputId, name, isSearchable, error, noOptionsMessage, t });

  const composedKeyDown = useCallback((event) => {
    handleKeyDown(event);
    if (consumerOnKeyDown) consumerOnKeyDown(event);
  }, [handleKeyDown, consumerOnKeyDown]);

  const composedMenuOpen = useCallback(() => {
    handleMenuOpen();
    if (consumerOnMenuOpen) consumerOnMenuOpen();
  }, [handleMenuOpen, consumerOnMenuOpen]);

  const composedMenuClose = useCallback(() => {
    handleMenuClose();
    if (consumerOnMenuClose) consumerOnMenuClose();
  }, [handleMenuClose, consumerOnMenuClose]);

  // react-select v3 Creatable has a reference-equality bug: when the menu
  // re-renders between mouse hover and click (frequent while typing), the
  // clicked option's reference no longer matches state.newOption, so the new
  // option leaks through onChange instead of triggering onCreateOption. Route
  // any __isNew__ value back to onCreateOption to avoid setting the typed
  // text as a "selected" value.
  const handleCreatableChange = useCallback((newValue, actionMeta) => {
    const candidate = Array.isArray(newValue) ? newValue[newValue.length - 1] : newValue;
    if (candidate?.__isNew__ && onCreateOption) {
      onCreateOption(candidate.value);
      return;
    }
    if (onChange) onChange(newValue, actionMeta);
  }, [onChange, onCreateOption]);

  const formatGroupLabel = (data) => (
    <div className="select-customizable__group-label">
      <span>{data.label}</span>
      <span className="select-customizable__group-badge">{data.options.length}</span>
    </div>
  );

  function getCreateLabel(text) {
    return `${t('placeholder:Create new section')}: ${text}`;
  }

  function getOptionLabel(data, { context }) {
    return useShortLabels && context === 'value' ? data.shortLabel : data.label;
  }

  const getLabelTitle = (labelValue) => {
    if (typeof labelValue === 'string') return labelValue;

    // Si es un elemento React con children como string
    if (React.isValidElement(labelValue) && typeof labelValue.props.children === 'string') {
      return labelValue.props.children;
    }

    // Si es un elemento React con children como array (múltiples elementos)
    if (React.isValidElement(labelValue) && Array.isArray(labelValue.props.children)) {
      return labelValue.props.children.filter((child) => typeof child === 'string').join(' ');
    }

    return '';
  };

  const sharedProps = {
    _listboxId: listboxId,
    _focusedOptionId: focusedOptionId,
    _onOptionFocusChange: onOptionFocusChange,
    onKeyDown: composedKeyDown,
    onMenuOpen: composedMenuOpen,
    onMenuClose: composedMenuClose,
    tabSelectsValue: false,
    // Props needed by module-level custom components via selectProps
    iconTypeIndicator,
    showValuesAsList,
    showValuesAsText,
    useShortLabels,
  };

  return (
    <>
      {label && (
        <label
          className="select-customizable__label"
          title={getLabelTitle(label)}
          id={labelId}
        >
          {label} {props.required && <sup>*</sup>}
        </label>
      )}
      {canCreate ? (
        <CreatableSelect
          ref={selectRef}
          menuPosition={'fixed'}
          menuPlacement={finalMenuPlacement}
          options={options}
          className={`select-customizable select-customizable--${variant} select-customizable--size-${size}${error ? ' select-customizable--error' : ''}`}
          classNamePrefix="select-customizable"
          defaultValue={defaultValue}
          value={value}
          placeholder={placeholder}
          inputId={resolvedInputId}
          aria-labelledby={labelId}
          components={{
            DropdownIndicator,
            Control: AccessibleControl,
            Input: AccessibleInput,
            Menu: AccessibleMenu,
            Option: AccessibleOption,
          }}
          noOptionsMessage={getNoOptionsMessage}
          isMulti={isMulti}
          onChange={handleCreatableChange}
          onCreateOption={onCreateOption}
          createOptionPosition="last"
          isClearable={true}
          formatCreateLabel={getCreateLabel}
          name={name}
          styles={sharedStyles}
          {...props}
          {...sharedProps}
        />
      ) : (
        <Select
          ref={selectRef}
          menuPosition={menuPosition}
          menuPlacement={finalMenuPlacement}
          options={options}
          className={`select-customizable select-customizable--${variant} select-customizable--size-${size}${error ? ' select-customizable--error' : ''}`}
          classNamePrefix={`${showValuesAsList ? 'select-customizable--show-values-as-list' : 'select-customizable'}`}
          defaultValue={defaultValue}
          value={value}
          placeholder={placeholder}
          inputId={resolvedInputId}
          aria-labelledby={labelId}
          components={{
            DropdownIndicator,
            Control: AccessibleControl,
            Input: AccessibleInput,
            Menu: AccessibleMenu,
            Option: AccessibleOption,
            ...((showValuesAsList || showValuesAsText) && {
              MultiValue: MultiValueContainer,
              ValueContainer,
            }),
          }}
          noOptionsMessage={getNoOptionsMessage}
          isMulti={isMulti}
          onChange={onChange}
          name={name}
          menuPortalTarget={menuPortalTarget}
          formatGroupLabel={formatGroupLabel}
          hideSelectedOptions={hideSelectedOptions}
          formatOptionLabel={getOptionLabel}
          styles={{
            ...sharedStyles,
            ...(showValuesAsList && {
              control: (base, state) => {
                const shared = sharedStyles.control(base, state);
                if (state.hasValue && !state.isFocused) {
                  return {
                    ...shared,
                    border: '1px solid var(--select-customizable-border-default)',
                    boxShadow: 'none',
                  };
                }
                return shared;
              },
              valueContainer: (base) => ({
                ...base,
                padding: '2px 8px',
                overflow: 'hidden',
                cursor: 'pointer',
              }),
            }),
          }}
          isSearchable={finalIsSearchable}
          {...props}
          {...sharedProps}
        />
      )}
      {error && <span className="select-customizable__error">{error}</span>}
    </>
  );
};

SelectCustomizable.propTypes = {
  isMulti: PropTypes.bool,
  options: PropTypes.array,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  noOptionsMessage: PropTypes.string,
  onChange: PropTypes.func,
  variant: PropTypes.oneOf(['default']),
  size: PropTypes.oneOf(['default', 'small']),
  name: PropTypes.string,
  inputId: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isSearchable: PropTypes.bool,
  iconTypeIndicator: PropTypes.string,
  menuPortalTarget: PropTypes.object,
  useShortLabels: PropTypes.bool,
  hideSelectedOptions: PropTypes.bool,
  showValuesAsList: PropTypes.bool,
  showValuesAsText: PropTypes.bool,
  canCreate: PropTypes.bool,
  onCreateOption: PropTypes.func,
  menuPosition: PropTypes.string,
  error: PropTypes.string,
  defaultValue: PropTypes.any,
  value: PropTypes.any,
  t: PropTypes.func,
  onKeyDown: PropTypes.func,
  onMenuOpen: PropTypes.func,
  onMenuClose: PropTypes.func,
};

SelectCustomizable.defaultProps = {
  isMulti: false,
  options: [],
  variant: 'default',
  size: 'default',
  menuPosition: 'fixed',
  onChange: () => {
    return null;
  },
};
export default withTranslation(['placeholder'])(SelectCustomizable);
